import { useMemo, useState } from 'react';
import { Dimensions, PackageApiModel } from '~/lib/api/smartsend/types';
import { AutoPackageList } from '../package/AutoPackageList';
import { DimensionAndWeightDisplay, DimensionDisplay } from './DimensionDisplay';
import { DimensionsAreValid } from './panels/DimensionsAreValid';
import GetWeightOptions from './GetWeightOptions';
import {
  P2GBox,
  P2GButton,
  P2GFade,
  P2GGrid,
  P2GLink,
  P2GLinkGroup,
  P2GText,
  P2GTextField,
  useP2GMediaQueryOnly
} from '@parcel2go/core-ui';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { CentimetreAbbreviation } from '~/components/MeasurementUnits/MeasurementUnit';

interface props {
  onSave?: (dimensions: Dimensions & { Name?: string }) => void;
  onUpdate?: (dimensions: Dimensions & { Name?: string }) => void;
  dimensions: Dimensions;
  startMinimised: boolean;
  fallback?: Dimensions;
  dimensionsRequired?: boolean;
  getPackages?: () => Promise<PackageApiModel[]>;
  hideDimensionsIfNotRequired?: boolean;
  hideWeight?: boolean;
  showWhenTouched?: boolean;
  children?: React.ReactNode;
}

export const DimensionsSelection: React.FC<props> = (props) => {
  const [touched, setTouched] = useState(false);
  const [staging, setStaging] = useState<Dimensions>({ ...props.dimensions });
  const [expand, setExpand] = useState(!props.startMinimised);
  const isXs = useP2GMediaQueryOnly('xs');
  const { t } = useTranslation([TranslationNamespaces.SMARTSEND, TranslationNamespaces.COMMON]);

  const weightOptions = useMemo(() => GetWeightOptions(isXs || props.showWhenTouched), [isXs, props.showWhenTouched]);

  const showDims = useMemo(() => {
    if (props.hideDimensionsIfNotRequired && !props.dimensionsRequired) {
      return false;
    }
    return true;
  }, [props.dimensions, props.hideDimensionsIfNotRequired, props.dimensionsRequired]);

  const onUpdate = (dims: Dimensions & { Name?: string }) => {
    setTouched(true);
    setStaging(dims);
    if (props.onUpdate) {
      props.onUpdate(dims);
    }
  };

  const display = (w: number) => {
    return w < 1 ? `${w * 1000}g` : `${w}kg`;
  };

  if (!expand) {
    return (
      <P2GBox mt={1}>
        <P2GGrid container spacing={2} alignItems="center" alignContent="center">
          <P2GGrid item flexShrink={1}>
            <P2GText variant="body2">{t('PARCEL_SIZE_TEXT', { defaultValue: 'Parcel Size:' })}</P2GText>
          </P2GGrid>
          <P2GGrid item flexShrink={1}>
            <DimensionAndWeightDisplay variant="body2" dimensions={props.dimensions} />
          </P2GGrid>
          <P2GGrid item flexShrink={1}>
            <P2GLink onClick={() => setExpand(true)}>{t('EDIT_LINK', { defaultValue: 'Edit' })}</P2GLink>
          </P2GGrid>
        </P2GGrid>
      </P2GBox>
    );
  }

  const cm = t(CentimetreAbbreviation.key, CentimetreAbbreviation.options);
  return (
    <P2GBox>
      {!props.hideWeight && (
        <P2GGrid container spacing={2} alignItems="center">
          <P2GGrid item sm={4} xs={12}>
            <P2GTextField
              fullWidth
              label={t('WEIGHT_INPUT_LABEL', { defaultValue: 'Weight' })}
              type="number"
              required
              showErrorsWhenPristine={true}
              value={(staging.Weight ?? '').toString()}
              onChange={(v) => onUpdate({ ...staging, Weight: Number(v.currentTarget.value) })}
              endAdornment={'kg'}
              step={0.01}
              max={2500}
              min={0.0}
            />
          </P2GGrid>
          <P2GGrid item sm={8} xs={12}>
            <P2GLinkGroup
              links={weightOptions.map((w) => (
                <P2GLink variant="body2" key={w} onClick={() => onUpdate({ ...staging, Weight: w })}>
                  {w == staging.Weight ? <strong>{display(w)}</strong> : display(w)}
                </P2GLink>
              ))}
            />
          </P2GGrid>
        </P2GGrid>
      )}
      {showDims && (
        <P2GBox pt={props.hideWeight ? 0 : 2}>
          <AutoPackageList
            selected={staging}
            packages={props.getPackages}
            onSelect={(p) => onUpdate({ ...p, Weight: p.Weight || staging.Weight })}
          >
            <P2GGrid item xs={4}>
              <P2GTextField
                fullWidth
                label={t('LENGTH_INPUT_LABEL', { defaultValue: 'Length' })}
                type="number"
                required={!!props.dimensionsRequired}
                value={(staging.Length ?? '').toString()}
                onChange={(v) => onUpdate({ ...staging, Length: Number(v.currentTarget.value) })}
                endAdornment={cm}
                step={1}
                max={300}
                showErrorsWhenPristine={true}
              />
            </P2GGrid>
            <P2GGrid item xs={4}>
              <P2GTextField
                fullWidth
                label={t('WIDTH_INPUT_LABEL', { defaultValue: 'Width' })}
                type="number"
                required={!!props.dimensionsRequired}
                value={(staging.Width ?? '').toString()}
                onChange={(v) => onUpdate({ ...staging, Width: Number(v.currentTarget.value) })}
                endAdornment={cm}
                step={1}
                max={300}
                showErrorsWhenPristine={true}
              />
            </P2GGrid>
            <P2GGrid item xs={4}>
              <P2GTextField
                fullWidth
                label={t('HEIGHT_INPUT_LABEL', { defaultValue: 'Height' })}
                type="number"
                required={!!props.dimensionsRequired}
                value={(staging.Height ?? '').toString()}
                onChange={(v) => onUpdate({ ...staging, Height: Number(v.currentTarget.value) })}
                endAdornment={cm}
                step={1}
                max={300}
                showErrorsWhenPristine={true}
              />
            </P2GGrid>
          </AutoPackageList>
        </P2GBox>
      )}
      {props.fallback && props.fallback.Height > 0 && props.fallback.Width > 0 && props.fallback.Length > 0 && (
        <P2GLinkGroup
          pt={1}
          links={[
            <P2GLink onClick={() => setStaging({ ...props.fallback, Weight: staging.Weight })}>
              <Trans
                t={t}
                i18nKey="SET_AS_FALLBACK_LINK"
                values={{
                  dimension: DimensionDisplay(props.fallback)
                }}
                defaults="Set as {{dimension}}"
              />
            </P2GLink>
          ]}
        />
      )}
      {props.onSave && (!props.showWhenTouched || touched) && (
        <P2GBox mt={2}>
          <P2GFade in timeout={1000}>
            <P2GButton
              disabled={!DimensionsAreValid(staging, props.dimensionsRequired)}
              onClick={() => props.onSave(staging)}
              variant="primary"
            >
              {t('SAVE_DIMENSIONS_BUTTON', { defaultValue: 'Save Dimensions' })}
            </P2GButton>
          </P2GFade>
        </P2GBox>
      )}
    </P2GBox>
  );
};
